import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AMLSummary from '../../../components/AMLSummary/AMLSummary';
import AdaptiveButton from '../../../components/AdaptiveButton';
import Copy from '../../../components/Copy';
import HistoryBlock from '../../../components/HistoryBlock';
import Icon, { Icons } from '../../../components/Icon/Icon';
import AppConfig from '../../../config';
import { usePageContext } from '../../../hooks/usePageContext';
import { t } from '../../../lang';
import HelperService from '../../../services/HelperService';
import TypesService from '../../../services/TypesService';
import { receiveSlice, sendSlice } from '../../../store/slices';
import './AddressSlide.scss';

const CurrenciesById = TypesService.CurrenciesById;
const { routes } = AppConfig;
const ICON_SIZE_HEADER_TITLE = 20;

const AddressSlide = ({ address, pagination, loading }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { useSearchParams, useSelector } = usePageContext();

	const availableCurrencies = useSelector(store => store.app.availableCurrencies);
	const currencyId = Number(useSearchParams().get('currencyId'));
	const currencyData = CurrenciesById[address.CurrencyId];
	const currencySymbol = availableCurrencies.find(({ Id }) => Id === currencyId)?.Symbol;

	const goToSend = () => {
		dispatch(sendSlice.actions.setAccount(address));
		dispatch(
			sendSlice.actions.setAsset({
				Id: currencyId,
				Symbol: currencySymbol,
				BlockchainId: currencyData.BlockchainId,
			})
		);
		navigate(routes.sendRecipient, { state: { wentToSendFromParams: { location } } });
	};

	const goToReceive = () => {
		dispatch(receiveSlice.actions.setAccount(address));
		dispatch(receiveSlice.actions.setAsset({ Symbol: currencySymbol, BlockchainId: currencyData.BlockchainId }));
		navigate(routes.receiveQR);
	};

	const goToSwap = () => {
		navigate(
			{ pathname: routes.stubPage },
			{
				state: {
					header: 'Swap',
					text: 'Swap is currently unavailable, but we are working on getting it up and running',
					labelIcon: 'ARROW_TRIANGLEHEAD_SWAP_CIRCLE',
				},
			}
		);
	};

	return (
		<div className='AddressSlide'>
			<div className='AddressSlide__main-block'>
				<div className='AddressSlide__info'>
					<div className='AddressSlide__info-title'>
						<Icon type={Icons.QR_CODE} width={ICON_SIZE_HEADER_TITLE} height={ICON_SIZE_HEADER_TITLE} />
						<div className='AddressSlide__info-lable'>{address.AddressLabel}</div>
					</div>
					<div className='AddressSlide__info-crypto'>
						{HelperService.FormatCrypto(loading ? 0 : address.Amount, currencyData?.Precision)}
					</div>
					<div className='AddressSlide__info-fiat'>
						${HelperService.FormatFiatWithoutCurrency(loading ? 0 : address.AmountUsd)}
					</div>
				</div>
				{pagination}
				<div className='AddressSlide__buttons'>
					<div className='AddressSlide__button AddressSlide__button-send' onClick={goToSend}>
						<AdaptiveButton icon='ARROW' />
						<div>{t('Send')}</div>
					</div>
					<div className='AddressSlide__button AddressSlide__button-receive' onClick={goToReceive}>
						<AdaptiveButton icon='ARROW' />
						<div>{t('Receive')}</div>
					</div>
					<div className='AddressSlide__button' onClick={() => goToSwap()}>
						<AdaptiveButton icon='ARROW_TRIANGLEHEAD_SWAP' />
						<div>{t('Swap')}</div>
					</div>
					<div className='AddressSlide__button AddressSlide__button-more'>
						<AdaptiveButton icon='SETTINGS_ELLIPSIS' />
						<div>{t('More')}</div>
					</div>
				</div>

				<Copy
					loading={loading}
					title={t('My address')}
					description={address.Address || ''}
					copyText={address.Address || ''}
				/>
			</div>

			<div className='AddressSlide__body'>
				{address.BlockchainId !== null && (
					<AMLSummary
						className='FormBlock'
						percent={address.RiskScore}
						blockchainId={address.BlockchainId}
						showBlockchain={true}
						highRisk={address.HighRisk}
						isActualAML={address.IsActualAml}
						date={address.LastCheckDate}
						address={address.Address}
						checkDate={address.LastCheckDate}
					/>
				)}

				<HistoryBlock addressId={address.AddressId} useAsSkeleton={loading} />
			</div>
		</div>
	);
};

export default AddressSlide;
